/**
 * "View as learner" preference — localStorage-backed, subscribable.
 *
 * Admins toggle this from the sidebar (preview the dashboard as a
 * learner) or via cleanup routes that need to clear it on logout.
 * Course.tsx subscribes via `useViewAsLearner()` and derives its
 * `isAdminView` flag from the result; AdminCourseFilters and the
 * page header used to own a local Switch but the affordance has
 * been lifted to the sidebar for global reach.
 *
 * Implementation note: we publish a tiny in-memory listener set so
 * any component using `useViewAsLearner()` re-renders the instant
 * another component flips the flag, without polling localStorage
 * or wiring through Redux. Cross-tab sync isn't required (it's a
 * per-tab preview affordance), so we skip the `storage` event hook.
 */

import { useSyncExternalStore } from 'react';

const KEY = 'lms.viewAsLearner';
const listeners = new Set<() => void>();

export function getViewAsLearner(): boolean {
  try {
    return localStorage.getItem(KEY) === 'true';
  } catch {
    return false;
  }
}

export function setViewAsLearner(value: boolean): void {
  try {
    if (value) localStorage.setItem(KEY, 'true');
    else localStorage.removeItem(KEY);
  } catch {
    /* quota or unavailable — UI still reacts via listeners, just
       won't persist across reload. Acceptable for a preview flag. */
  }
  listeners.forEach((l) => {
    try { l(); } catch { /* listener error — don't break siblings */ }
  });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Reactive hook — components re-render the moment any caller flips
 *  the flag via `setViewAsLearner()`. SSR fallback returns false. */
export function useViewAsLearner(): boolean {
  return useSyncExternalStore(subscribe, getViewAsLearner, () => false);
}
